import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, handleApiError } from '../../../services/api';
import './BulkMessageComposer.css';

const BulkMessageComposer = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null); // { type: 'success'|'error', text: '' }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePreviewSubmit = (e) => {
    e.preventDefault();
    if (!formData.message.trim()) {
      setSubmitMessage({ type: 'error', text: t('bulkMessages.requiredMessage', 'Message content is required.') });
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSendBulkMessage = async () => {
    setIsSubmitting(true);
    setSubmitMessage(null);
    setShowConfirmModal(false);
    
    try {
      await adminAPI.broadcastChatMessage(formData);
      setSubmitMessage({ type: 'success', text: t('bulkMessages.success', 'Bulk message broadcast has been queued and is being sent.') });
      setFormData({
        message: ''
      });
      // clear success message after 5 seconds
      setTimeout(() => setSubmitMessage(null), 8000);
    } catch (error) {
      setSubmitMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bulk-message-composer">
      <div className="composer-header">
        <h3>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          {t('bulkMessages.title', 'Broadcast Chat Message')}
        </h3>
        <p>{t('bulkMessages.description', 'Send a direct chat message to all registered users. This will appear in their private inbox.')}</p>
      </div>

      {submitMessage && (
        <div className={`composer-alert alert-${submitMessage.type}`}>
          {submitMessage.text}
        </div>
      )}

      <form className="composer-form" onSubmit={handlePreviewSubmit}>
        <div className="form-group">
          <label htmlFor="message">{t('bulkMessages.formMessage', 'Message Content')} <span className="required">*</span></label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={t('bulkMessages.messagePlaceholder', 'Write the message you want to send to everyone...')}
            disabled={isSubmitting}
            required
            rows={10}
            maxLength={5000}
          />
          <div className="char-counter">
            {formData.message.length} / 5000
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="loading-spinner-small"></span>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                {t('bulkMessages.sendBtn', 'Send to All Users')}
              </>
            )}
          </button>
        </div>
      </form>

      {showConfirmModal && (
        <div className="announcement-modal-overlay">
          <div className="announcement-modal">
            <div className="modal-header">
              <h4>{t('bulkMessages.confirmTitle', 'Confirm Bulk Message')}</h4>
              <button 
                className="close-btn" 
                onClick={() => setShowConfirmModal(false)}
                title={t('common.close', 'Close')}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>{t('bulkMessages.confirmText', 'Are you sure you want to send this chat message to ALL users? This will create individual conversations in their inboxes.')}</p>
              
              <div className="message-preview-box">
                <p>{formData.message}</p>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setShowConfirmModal(false)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button 
                className="btn-confirm" 
                onClick={confirmSendBulkMessage}
              >
                {t('bulkMessages.confirmSend', 'Yes, Send Now')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkMessageComposer;
